/**
 * IN-FLIGHT RECONSTRUCTION ON RELOAD — the consumer half of mid-turn visibility.
 *
 * WHY THIS IS ITS OWN MODULE
 * Persisting the tool row mid-turn is only half the fix. The reload path used to gate the ENTIRE
 * in-flight reconstruction on `if (data.partialText)` and consult `isSessionBusy` only INSIDE that
 * branch. A TOOL-HEAVY turn has NO partialText — `partialDirty` is set exclusively in `onChunk`,
 * and an agentic turn can run Bash/gh/CI for ten minutes without emitting a single character. So on
 * the exact Atlas shape the row could be written perfectly and read by NOBODY: the composer
 * reconstructed a LIVE turn as IDLE, and the transcript showed a frozen ten-minute-old bubble.
 *
 * Busy is now armed from `isSessionBusy` ALONE — server-authoritative, never inferred from the
 * presence of text, and never synthesized from the mere existence of a row.
 *
 * It lives outside the useSocket closure so the setpoint eval can drive the REAL decision rather
 * than a re-implementation of it (the precedent: query-eviction-decision.ts, rebuild-decision.ts).
 * A decision that can only be exercised through a socket handler is a decision no eval can pin.
 *
 * Pure: no Preact, no store, no socket. Callers apply the result.
 */

export interface InflightLoadPayload {
  partialText?: string;
  partialReason?: string;
  isSessionBusy?: boolean;
  /**
   * WHICH chat_message row is the live turn's tool trace. Comes from the server (the flusher
   * pre-generates it), so it is KNOWN, not guessed. A "last tool row" heuristic would mis-claim
   * the PREVIOUS turn's completed row during the window before this turn's first flush — which
   * would suppress it from the transcript and graft its tools into the current turn.
   */
  inflightToolRowId?: string;
}

export interface LoadedRow {
  id?: string;
  role: string;
  text: string;
}

export interface ToolActivityLike {
  type: string;
  tool: string;
  input?: string;
}

export interface InflightReconstruction {
  /** The composer must read BUSY. Server truth — not "there is text", not "there is a row". */
  armBusy: boolean;
  /** Seed for the streaming buffer, so live chunks append to the full text (only when text exists). */
  seedText?: string;
  /** The reconstructed live bubble. Absent when the turn has streamed no text (a tool-heavy turn). */
  streamingBubble?: { role: 'assistant'; text: string; streaming: true };
  /** The abandoned-partial bubble, framed by partialReason. Only for a turn that is NOT running. */
  systemBubble?: { role: 'system'; text: string };
  /** The live turn's tool row — the renderer suppresses it, because the live trace now owns it. */
  inflightToolRowId?: string;
  /** Its activities, handed to the live in-memory trace so ONE trace keeps growing after a reload. */
  seedToolActivities?: ToolActivityLike[];
}

/**
 * Should the renderer HIDE this persisted tool row, because the live in-memory trace already owns
 * that turn's content?
 *
 * ── WHY THIS IS BUSY-GATED, AND WHY THAT IS THE WHOLE POINT ──
 * The suppression exists for exactly one reason: after a mid-turn reload, the live trace is SEEDED
 * FROM this very row and keeps growing, so rendering the row too would show the same turn twice.
 * But the live trace renders **iff `busy`** (`ChatArea`: `busy && toolActivities.length > 0`).
 *
 * So the two predicates MUST be the same predicate. When they were allowed to differ, they
 * disagreed in the one direction that costs the user everything — NEITHER rendering:
 *
 *   reload mid-turn -> id armed, live trace shows (busy)      ... correct
 *   turn completes  -> busy=false -> live trace STOPS rendering
 *                      but the id was never cleared, so the persisted row stayed SUPPRESSED
 *                   => the entire 90-minute tool trace VANISHES at the completion boundary,
 *                      and stays gone for the life of the page (tabCache is an in-memory useRef;
 *                      every later refetch re-delivers the row and it is suppressed again).
 *
 * That is a regression in precisely the flow this feature exists to fix, so it is pinned by an SE
 * (SE-10) rather than left to a comment. Clearing the id on every terminal path is ALSO done — but
 * it is the belt, not the braces: this gate is what makes "hidden" impossible to outlive "live".
 *
 * The `busy` gate additionally kills the stale-id hazard in the other direction: a NEXT turn goes
 * busy while the id still names the PREVIOUS turn's row, which would suppress real, completed
 * history. Clearing on CLAUDE_DONE closes that; this gate means a missed clear cannot be fatal.
 */
export function shouldSuppressToolRow(
  msgId: string | undefined,
  inflightToolRowId: string | undefined,
  busy: boolean,
): boolean {
  if (!busy) return false;                     // nothing is live => nothing may be hidden
  if (!inflightToolRowId || !msgId) return false;
  return msgId === inflightToolRowId;          // matched BY ID, never by position
}

/** Framing for a partial left behind by a turn that is no longer running. Moved verbatim from useSocket. */
export function partialReasonLabel(reason?: string): string {
  switch (reason) {
    case 'zombie_eviction':
      return '⚠ Previous query was abandoned (took too long). The saved partial is shown below:';
    case 'context_reset':
      return 'ℹ Session refreshed — agent continued in a new context window. Partial saved:';
    case 'handoff':
      return 'ℹ Session handed off. Partial saved:';
    case 'auto_continuation':
      return 'ℹ Session refreshed — agent continued. Partial saved:';
    case 'sdk_crash':
      return '⚠ Agent crashed mid-response. Please retry. Partial saved:';
    case 'hot_reload':
      return 'ℹ Backend reloaded — last response may be incomplete. Partial saved:';
    case 'container_restart':
      return '⚠ Server restarted mid-response. The saved partial is shown below:';
    default:
      return 'ℹ Connection was lost mid-response. The saved partial is shown below:';
  }
}

export function reconstructInflightOnLoad(
  data: InflightLoadPayload,
  loadedMessages: LoadedRow[],
): InflightReconstruction {
  // ── A TURN IS RUNNING ────────────────────────────────────────────────────────────────────────
  if (data.isSessionBusy) {
    const r: InflightReconstruction = { armBusy: true };

    if (data.partialText) {
      // Narration exists — reconstruct it as the (single) streaming bubble, and seed the buffer so
      // incoming CLAUDE_CHUNKs append to the full text rather than restarting from the new chunks.
      r.seedText = data.partialText;
      r.streamingBubble = { role: 'assistant', text: data.partialText, streaming: true };
    }
    // Deliberately NO empty streaming bubble when there is no text: it would be a second streaming
    // placeholder (against the single-streaming-bubble invariant) and an empty role\0text identity
    // key in the snapshot/live-tail merge. The liveness signal is the armed busy state + the
    // persistent working indicator + the tool trace — not a fabricated bubble.

    // Hand the live turn's tool row to the in-memory trace, so after a reload there is ONE trace
    // that keeps growing — rather than a frozen row from the DB sitting beside a fresh live one.
    // Matched BY ID (see InflightLoadPayload.inflightToolRowId). Before the first flush the id is
    // absent and nothing is seeded or suppressed, which is exactly right: there is nothing yet.
    if (data.inflightToolRowId) {
      const row = loadedMessages.find((m) => m.id === data.inflightToolRowId && m.role === 'tool');
      if (row) {
        try {
          const parsed = JSON.parse(row.text);
          if (Array.isArray(parsed) && parsed.length > 0) {
            r.inflightToolRowId = data.inflightToolRowId;
            r.seedToolActivities = parsed as ToolActivityLike[];
          }
        } catch {
          // An unreadable row must never cost us the busy signal. Leave it to render as-is.
        }
      }
    }
    return r;
  }

  // ── NO TURN IS RUNNING ───────────────────────────────────────────────────────────────────────
  // A dead turn must never be reconstructed as busy — that would be a false liveness claim
  // synthesized by the client. Show the partial, framed by why it stopped.
  if (data.partialText) {
    return {
      armBusy: false,
      systemBubble: {
        role: 'system',
        text: `${partialReasonLabel(data.partialReason)}\n\n${data.partialText}`,
      },
    };
  }

  return { armBusy: false };
}
